import { useEffect, useState, type ReactNode } from 'react';
import { Check, Copy, Download } from 'lucide-react';
import { btnSmall } from '@/components/svg-tools/ToolParts';

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Older browsers, or a page without clipboard permission: copy through a hidden textarea.
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch {
      ok = false;
    }
    ta.remove();
    return ok;
  }
}

/** Save text as a file, made in the browser. */
export function downloadText(text: string, fileName: string, type: string) {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function CopyButton({ text, label = 'Copy' }: { text: string; label?: string }) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  useEffect(() => {
    if (state === 'idle') return;
    const t = setTimeout(() => setState('idle'), 1600);
    return () => clearTimeout(t);
  }, [state]);
  return (
    <button
      type="button"
      className={btnSmall}
      onClick={async () => setState((await copyText(text)) ? 'copied' : 'failed')}
      aria-live="polite"
    >
      {state === 'copied' ? <Check className="h-3.5 w-3.5 text-sky" aria-hidden="true" /> : <Copy className="h-3.5 w-3.5" aria-hidden="true" />}
      {state === 'copied' ? 'Copied' : state === 'failed' ? 'Press Ctrl+C' : label}
    </button>
  );
}

/** A titled, read-only block of generated code with Copy and, optionally, Download. */
export function CodeOutput({
  label,
  code,
  meta,
  download,
  wrap = false,
  heightClass = 'max-h-[360px]',
}: {
  label: string;
  code: string;
  meta?: ReactNode;
  download?: { fileName: string; type: string };
  /** Break long lines (data URIs) instead of scrolling sideways. */
  wrap?: boolean;
  heightClass?: string;
}) {
  return (
    <div className="min-w-0 overflow-hidden rounded-xl border border-cream-200 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-cream-200 bg-cream-50 px-3 py-2">
        <div className="min-w-0">
          <p className="text-[13px] font-semibold text-ink">{label}</p>
          {meta && <p className="text-[12px] text-ink-muted">{meta}</p>}
        </div>
        <div className="flex gap-1.5">
          <CopyButton text={code} />
          {download && (
            <button type="button" className={btnSmall} onClick={() => downloadText(code, download.fileName, download.type)}>
              <Download className="h-3.5 w-3.5" aria-hidden="true" />
              Download
            </button>
          )}
        </div>
      </div>
      <pre
        tabIndex={0}
        className={`${heightClass} overflow-auto px-3 py-2.5 font-mono text-[12px] leading-[1.6] [font-variant-ligatures:none] text-ink ${
          wrap ? 'whitespace-pre-wrap break-all' : 'whitespace-pre'
        }`}
      >
        <code>{code}</code>
      </pre>
    </div>
  );
}
